import type { RequestFn } from '../http';

/**
 * DNS-Zonen (v1.408.0): Cloudflare-Zonen beider FixByte-Konten, alle 15 Minuten abgeglichen und
 * versioniert. Backend: services/dnsZones/, routes/dnsZones.ts. Lesen für alle Mitarbeiter,
 * Zuordnung freigeben nur Admin/Teamleitung (Server prüft die Rolle).
 */

export type DnsZoneAssignmentStatus = 'UNASSIGNED' | 'SUGGESTED' | 'CONFIRMED' | 'REJECTED' | 'INTERNAL';
export type DnsZoneDecision = 'ACCEPT' | 'REJECT' | 'ASSIGN' | 'INTERNAL' | 'UNASSIGN';

export interface DnsZoneCustomerRef {
  id: string;
  name: string;
  number: string | null;
}

export interface DnsZoneListItem {
  id: string;
  name: string;
  accountKey: string;
  accountLabel: string;
  cloudflareZoneId: string;
  cloudflareStatus: string | null;
  nameServers: string[];
  presentInAccount: boolean;
  missingSince: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  currentVersion: number;
  lastChangedAt: string | null;
  assignment: {
    status: DnsZoneAssignmentStatus;
    customer: DnsZoneCustomerRef | null;
    suggestedCustomer: DnsZoneCustomerRef | null;
    suggestedInternal: boolean;
    suggestionReason: string | null;
    suggestionSource: string | null;
    suggestedAt: string | null;
    confirmedByUserId: string | null;
    confirmedAt: string | null;
  };
}

export interface DnsRecord {
  id: string | null;
  type: string;
  name: string;
  content: string | null;
  ttl: number | null;
  proxied: boolean | null;
  priority: number | null;
  data: unknown;
  comment: string | null;
  tags: string[];
}

export type DnsSection<T> = T | { unavailable: string };

export interface DnsZoneContent {
  format: number;
  zone: { name: string; status: string | null; nameServers: string[]; plan: string | null };
  records: DnsRecord[];
  settings: DnsSection<Record<string, unknown>>;
  pageRules: DnsSection<unknown[]>;
  workersRoutes: DnsSection<unknown[]>;
  firewallCustomRules: DnsSection<unknown[]>;
}

export interface DnsRecordChange {
  id: string | null;
  type: string;
  name: string;
  fields: string[];
  before: DnsRecord;
  after: DnsRecord;
}

export interface DnsZoneDiff {
  records: { added: DnsRecord[]; removed: DnsRecord[]; changed: DnsRecordChange[] };
  settings: Array<{ key: string; before: unknown; after: unknown }>;
  zone: Array<{ key: string; before: unknown; after: unknown }>;
  sections: Array<{ section: string; before: unknown; after: unknown }>;
  initial: boolean;
}

export interface DnsZoneVersionSummary {
  version: number;
  fetchedAt: string;
  recordCount: number;
  recordsChanged: boolean;
  contentHash: string;
  summary: { initial: boolean; added: number; removed: number; changed: number; settings: number; zone: number; sections: string[] } | null;
}

export interface DnsZoneAssignmentLogRow {
  id: string;
  action: string;
  fromStatus: string | null;
  toStatus: string;
  fromCustomerId: string | null;
  toCustomerId: string | null;
  actorLabel: string | null;
  source: string;
  reason: string | null;
  createdAt: string;
}

export interface DnsZoneDetail {
  zone: DnsZoneListItem;
  current: { version: number; fetchedAt: string; content: DnsZoneContent } | null;
  versions: DnsZoneVersionSummary[];
  assignmentLog: DnsZoneAssignmentLogRow[];
}

export interface DnsZoneAccount {
  key: string;
  label: string;
  configured: boolean;
  lastRunAt: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  zoneCount: number | null;
  running: boolean;
}

export interface DnsZoneFilter {
  customerId?: string;
  account?: string;
  status?: DnsZoneAssignmentStatus[];
  unassigned?: boolean;
  q?: string;
  missing?: boolean;
}

export const DNS_ZONES_QUERY_KEY = ['dns-zones'] as const;

function query(f: DnsZoneFilter): string {
  const p = new URLSearchParams();
  if (f.customerId) p.set('customerId', f.customerId);
  if (f.account) p.set('account', f.account);
  if (f.status?.length) p.set('status', f.status.join(','));
  if (f.unassigned) p.set('unassigned', 'true');
  if (f.q) p.set('q', f.q);
  if (f.missing) p.set('missing', 'true');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Download-Link (GET mit Cookie, kein CSRF nötig). */
export function dnsZoneExportUrl(zoneId: string, version: number, format: 'json' | 'bind'): string {
  return `/api/dns-zones/${encodeURIComponent(zoneId)}/versions/${version}/export?format=${format}`;
}

export function createDnsZonesApi(request: RequestFn) {
  return {
    async listDnsZones(filter: DnsZoneFilter = {}): Promise<{ zones: DnsZoneListItem[] }> {
      return request(`/dns-zones${query(filter)}`);
    },
    async getDnsZoneAccounts(): Promise<{ configured: boolean; accounts: DnsZoneAccount[] }> {
      return request('/dns-zones/accounts');
    },
    async getDnsZone(id: string): Promise<DnsZoneDetail> {
      return request(`/dns-zones/${encodeURIComponent(id)}`);
    },
    async getDnsZoneDiff(id: string, from: number, to: number): Promise<{ from: { version: number; fetchedAt: string }; to: { version: number; fetchedAt: string }; diff: DnsZoneDiff }> {
      return request(`/dns-zones/${encodeURIComponent(id)}/diff?from=${from}&to=${to}`);
    },
    async suggestDnsZoneAssignment(id: string, body: { customerId?: string; internal?: true; reason: string }): Promise<{ status: DnsZoneAssignmentStatus }> {
      return request(`/dns-zones/${encodeURIComponent(id)}/assignment-suggestion`, { method: 'POST', body: JSON.stringify(body) });
    },
    async decideDnsZoneAssignment(id: string, body: { action: DnsZoneDecision; customerId?: string; reason?: string }): Promise<{ status: DnsZoneAssignmentStatus; customerId: string | null }> {
      return request(`/dns-zones/${encodeURIComponent(id)}/assignment`, { method: 'POST', body: JSON.stringify(body) });
    },
  };
}

export const DNS_STATUS_LABEL: Record<DnsZoneAssignmentStatus, string> = {
  UNASSIGNED: 'nicht zugeordnet',
  SUGGESTED: 'Vorschlag offen',
  CONFIRMED: 'zugeordnet',
  REJECTED: 'Vorschlag abgelehnt',
  INTERNAL: 'FixByte intern',
};

export const DNS_STATUS_COLOR: Record<DnsZoneAssignmentStatus, string> = {
  UNASSIGNED: 'gray',
  SUGGESTED: 'yellow',
  CONFIRMED: 'green',
  REJECTED: 'red',
  INTERNAL: 'blue',
};

/** Anzeige eines Record-Inhalts (strukturierte Records wie SRV/CAA aus `data`). */
export function dnsRecordValue(r: { type: string; content: string | null; priority: number | null; data: unknown }): string {
  const d = (r.data && typeof r.data === 'object' ? r.data : null) as Record<string, unknown> | null;
  if (d && r.type === 'SRV' && d.target != null) return `${d.priority ?? r.priority ?? 0} ${d.weight ?? 0} ${d.port ?? 0} ${String(d.target)}`;
  if (d && r.type === 'CAA' && d.tag != null) return `${d.flags ?? 0} ${String(d.tag)} "${String(d.value ?? '')}"`;
  if (r.type === 'MX') return `${r.priority ?? 0} ${r.content ?? ''}`;
  return r.content ?? (d ? JSON.stringify(d) : '');
}

export function dnsTtlLabel(ttl: number | null): string {
  if (ttl == null || ttl === 1) return 'auto';
  if (ttl % 3600 === 0) return `${ttl / 3600} h`;
  if (ttl % 60 === 0) return `${ttl / 60} min`;
  return `${ttl} s`;
}
